import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Pencil } from "lucide-react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, DataTable, StatusBadge } from "../components/bits";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ROLES = ["admin", "secretaria", "contabilidad", "maestro", "padre"];
const EMPTY = { name: "", email: "", password: "", role: "secretaria", subject_ids: [], active: true };

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = useCallback(() => {
    api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(formatApiError(e)));
    api.get("/r/subjects").then((r) => setSubjects(r.data)).catch(() => {});
  }, [formatApiError]);
  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    try {
      const payload = { ...form };
      if (!payload.password) delete payload.password;
      if (editing) {
        await api.put(`/users/${editing.id}`, payload);
        toast.success("Usuario actualizado");
      } else {
        await api.post("/users", payload);
        toast.success("Usuario creado");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const columns = [
    { key: "name", label: "Nombre", render: (u) => <span className="font-medium text-slate-900">{u.name}</span> },
    { key: "email", label: "Correo" },
    { key: "role", label: "Rol", render: (u) => <span className="capitalize text-xs font-medium px-2.5 py-0.5 rounded-full bg-blue-50 text-blue-800 border border-blue-200">{u.role}</span> },
    { key: "active", label: "Estado", render: (u) => <StatusBadge value={u.active ? "activo" : "retirado"} /> },
    {
      key: "_a", label: "Acciones",
      render: (u) => (
        <Button size="sm" variant="ghost" data-testid={`edit-user-${u.id}`} onClick={() => { setEditing(u); setForm({ name: u.name, email: u.email, password: "", role: u.role, subject_ids: u.subject_ids || [], active: u.active }); setOpen(true); }}>
          <Pencil className="w-4 h-4" />
        </Button>
      ),
    },
  ];

  return (
    <div data-testid="users-page">
      <PageHeader title="Usuarios y roles" subtitle="Administración de accesos del colegio">
        <Button data-testid="create-user-button" className="bg-blue-800 hover:bg-blue-700" onClick={() => { setEditing(null); setForm(EMPTY); setOpen(true); }}>
          <Plus className="w-4 h-4 mr-1" /> Nuevo usuario
        </Button>
      </PageHeader>
      <DataTable columns={columns} rows={users} testid="users-table" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "Editar usuario" : "Nuevo usuario"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-1.5"><Label>Nombre</Label><Input data-testid="user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>Correo</Label><Input type="email" data-testid="user-email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} /></div>
            <div className="space-y-1.5"><Label>{editing ? "Nueva contraseña (opcional)" : "Contraseña"}</Label><Input type="password" data-testid="user-password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} /></div>
            <div className="space-y-1.5">
              <Label>Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                <SelectContent>{ROLES.map((r) => <SelectItem key={r} value={r} className="capitalize">{r}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            {form.role === "maestro" && (
              <div className="space-y-1.5">
                <Label>Materias asignadas</Label>
                <div className="border border-slate-200 rounded-lg p-3 max-h-40 overflow-y-auto space-y-2" data-testid="user-subjects">
                  {subjects.map((s) => (
                    <label key={s.id} className="flex items-center gap-2 text-sm cursor-pointer">
                      <input
                        type="checkbox"
                        checked={form.subject_ids.includes(s.id)}
                        onChange={() => setForm({ ...form, subject_ids: form.subject_ids.includes(s.id) ? form.subject_ids.filter((x) => x !== s.id) : [...form.subject_ids, s.id] })}
                      />
                      {s.name}
                    </label>
                  ))}
                </div>
              </div>
            )}
            <div className="flex items-center gap-3">
              <Switch data-testid="user-active-switch" checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} />
              <Label className="!mt-0">Usuario activo</Label>
            </div>
          </div>
          <div className="flex justify-end gap-2">
            <Button variant="outline" data-testid="cancel-user" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button data-testid="submit-user" className="bg-blue-800 hover:bg-blue-700" onClick={submit}>{editing ? "Guardar" : "Crear"}</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
